import { useMutation } from 'react-query';
import { useCallback } from 'react';
import { getEmployeeUseCases } from '@personnel/composition';
import type { EmployeePinValidateDto } from '@personnel/application/dtos/EmployeeRequestDto';
import { useApiErrorHandler } from './useApiErrorHandler';

/**
 * Hook for validating an employee PIN
 *
 * @returns {Object} Hook result with validatePin function and loading state
 * @returns {Function} returns.validatePin - Function that returns a boolean indicating if PIN is valid
 * @returns {boolean} returns.isValidatingPin - Whether PIN validation is in progress
 * @returns {Error|null} returns.error - Error if validation failed
 *
 * @example
 * const { validatePin, isValidatingPin } = useValidateEmployeePin();
 * const isValid = await validatePin({ pin: '1234', userUuid: 'user-uuid' }); // Returns boolean
 */
export const useValidateEmployeePin = () => {
  const { handleError } = useApiErrorHandler();
  const { validatePin: validatePinUseCase } = getEmployeeUseCases();

  const mutation = useMutation({
    mutationFn: (request: EmployeePinValidateDto) => validatePinUseCase.execute(request),
    onError: (error) => handleError(error),
  });

  /**
   * Validates a PIN and returns a boolean directly
   * Extracts the valid property from PinValidateResponseDto for convenience
   */
  const validatePin = useCallback(
    async (request: EmployeePinValidateDto): Promise<boolean> => {
      const response = await mutation.mutateAsync(request);
      return response.valid;
    },
    [mutation],
  );

  return {
    validatePin,
    isValidatingPin: mutation.isLoading,
    error: mutation.error,
  };
};
